"use client"

import { useState } from "react"
import {
  ArrowLeft,
  Download,
  Share2,
  Bookmark,
  TrendingUp,
  Target,
  BarChart3,
  FileText,
  CheckCircle,
  AlertTriangle,
  Info,
  Users,
  Lightbulb,
  Zap,
} from "lucide-react"
import "./DetailedBusinessInsightPage.css"
import BackButton from "../shared/backButton";

const DetailedBusinessInsightPage = ({ insightData }) => {
  const [activeSection, setActiveSection] = useState("overview")

  // Sample detailed insight data
  const getInsightDetails = () => {
    if (!insightData) {
      return getDefaultInsightData()
    }

    return {
      ...insightData,
      sections: {
        overview: {
          title: "Executive Summary",
          content: `This ${insightData.title.toLowerCase()} provides comprehensive evaluation of ${insightData.category === "employees" ? "employee" : "team"} performance metrics and strategic recommendations. The analysis covers key performance indicators, trend analysis, and actionable insights for decision-making.`,
          keyMetrics: Object.entries(insightData.metrics).map(([key, value]) => ({
            label: key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
            value: value,
            trend: key.includes("Performance") || key.includes("Score") ? "up" : "stable",
          })),
        },
        analysis: {
          title: "Detailed Analysis",
          content: `The ${insightData.title.toLowerCase()} examines multiple dimensions of ${insightData.category === "employees" ? "employee" : "team"} performance and identifies key patterns and opportunities for improvement.`,
          charts: [
            {
              title: `${insightData.category === "employees" ? "Employee" : "Team"} Performance Distribution`,
              type: "bar",
              data: [
                { label: "Excellent (90-100%)", value: 23, color: "#10b981" },
                { label: "Good (80-89%)", value: 45, color: "#3b82f6" },
                { label: "Average (70-79%)", value: 67, color: "#f59e0b" },
                { label: "Below Average (<70%)", value: 21, color: "#ef4444" },
              ],
            },
            {
              title: "Performance Trend (Last 12 Months)",
              type: "line",
              data: [
                { label: "Jan", value: 82 },
                { label: "Feb", value: 84 },
                { label: "Mar", value: 85 },
                { label: "Apr", value: 87 },
                { label: "May", value: 89 },
                { label: "Jun", value: 88 },
                { label: "Jul", value: 90 },
                { label: "Aug", value: 92 },
                { label: "Sep", value: 91 },
                { label: "Oct", value: 93 },
                { label: "Nov", value: 94 },
                { label: "Dec", value: 95 },
              ],
            },
          ],
        },
        insights: {
          title: "Key Insights & Findings",
          findings: [
            ...insightData.insights.map((insight, index) => ({
              type: index === 0 ? "positive" : index === 1 ? "info" : "warning",
              title: insight.split(" ").slice(0, 3).join(" ") + "...",
              description: insight,
              impact: index === 0 ? "High" : "Medium",
            })),
            {
              type: "positive",
              title: "Continuous Improvement",
              description: `${insightData.category === "employees" ? "Employees" : "Teams"} show consistent improvement over time with proper support and development opportunities.`,
              impact: "High",
            },
          ],
        },
        recommendations: {
          title: "Strategic Recommendations",
          items: [
            {
              priority: "high",
              title: `Enhance ${insightData.category === "employees" ? "Employee" : "Team"} Development Programs`,
              description: `Implement structured development initiatives focused on identified growth areas to maximize ${insightData.category === "employees" ? "employee" : "team"} potential.`,
              timeline: "3 months",
              expectedImpact: "Improve performance by 15%",
            },
            {
              priority: "medium",
              title: "Regular Performance Reviews",
              description: "Establish consistent feedback mechanisms to track progress and address challenges early.",
              timeline: "1 month",
              expectedImpact: "Increase engagement by 20%",
            },
            {
              priority: "high",
              title: "Knowledge Sharing Framework",
              description:
                "Create structured opportunities for sharing best practices and lessons learned across the organization.",
              timeline: "2 months",
              expectedImpact: "Accelerate skill development",
            },
          ],
        },
        implementation: {
          title: "Implementation Plan",
          phases: [
            {
              name: "Phase 1: Assessment & Planning",
              timeline: "Weeks 1-2",
              activities: [
                "Conduct detailed assessment of current state",
                "Define specific goals and success metrics",
                "Identify key stakeholders and resources",
                "Develop detailed implementation roadmap",
              ],
            },
            {
              name: "Phase 2: Initial Implementation",
              timeline: "Weeks 3-6",
              activities: [
                "Roll out pilot program with selected groups",
                "Collect initial feedback and make adjustments",
                "Prepare training materials and resources",
                "Establish monitoring mechanisms",
              ],
            },
            {
              name: "Phase 3: Full Deployment",
              timeline: "Weeks 7-12",
              activities: [
                "Scale implementation across organization",
                "Conduct training sessions for all participants",
                "Implement regular check-ins and progress reviews",
                "Begin data collection for impact assessment",
              ],
            },
          ],
        },
      },
    }
  }

  const getDefaultInsightData = () => {
    return {
      id: 1,
      title: "Top Performers Analysis Report",
      description: "Comprehensive evaluation of highest performing employees and their key success factors",
      category: "employees",
      type: "Performance",
      priority: "high",
      metrics: {
        topPerformers: 23,
        avgPerformance: "94%",
        retentionRate: "96%",
        promotionReady: 8,
      },
      insights: [
        "Top performers show 40% higher engagement scores",
        "Strong correlation between training completion and performance",
        "Leadership potential identified in 35% of top performers",
      ],
      sections: {
        overview: {
          title: "Executive Summary",
          content:
            "This comprehensive analysis provides detailed insights into our top-performing employees, identifying key success factors and patterns that drive exceptional performance across the organization.",
          keyMetrics: [
            { label: "Top Performers", value: "23", trend: "up" },
            { label: "Avg Performance", value: "94%", trend: "up" },
            { label: "Retention Rate", value: "96%", trend: "stable" },
            { label: "Promotion Ready", value: "8", trend: "up" },
          ],
        },
        analysis: {
          title: "Detailed Analysis",
          content:
            "The Top Performers Analysis examines multiple dimensions of employee performance and identifies key patterns and opportunities for improvement.",
          charts: [
            {
              title: "Performance Distribution",
              type: "bar",
              data: [
                { label: "Exceptional (95-100%)", value: 8, color: "#10b981" },
                { label: "Excellent (90-94%)", value: 15, color: "#3b82f6" },
                { label: "Good (80-89%)", value: 45, color: "#f59e0b" },
                { label: "Average (<80%)", value: 88, color: "#ef4444" },
              ],
            },
          ],
        },
        insights: {
          title: "Key Insights & Findings",
          findings: [
            {
              type: "positive",
              title: "High Engagement Correlation",
              description: "Top performers show 40% higher engagement scores compared to average performers.",
              impact: "High",
            },
            {
              type: "info",
              title: "Training Impact",
              description: "Strong correlation between training completion and performance metrics across departments.",
              impact: "Medium",
            },
            {
              type: "positive",
              title: "Leadership Potential",
              description:
                "Leadership potential identified in 35% of top performers, creating strong succession pipeline.",
              impact: "High",
            },
          ],
        },
        recommendations: {
          title: "Strategic Recommendations",
          items: [
            {
              priority: "high",
              title: "Enhance Development Programs",
              description:
                "Implement structured development initiatives focused on identified growth areas to maximize employee potential.",
              timeline: "3 months",
              expectedImpact: "Improve performance by 15%",
            },
          ],
        },
        implementation: {
          title: "Implementation Plan",
          phases: [
            {
              name: "Phase 1: Assessment & Planning",
              timeline: "Weeks 1-2",
              activities: [
                "Conduct detailed assessment of current state",
                "Define specific goals and success metrics",
                "Identify key stakeholders and resources",
                "Develop detailed implementation roadmap",
              ],
            },
          ],
        },
      },
    }
  }

  const insightDetails = getInsightDetails()

  const sections = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "analysis", label: "Analysis", icon: TrendingUp },
    { id: "insights", label: "Insights", icon: Lightbulb },
    { id: "recommendations", label: "Recommendations", icon: Target },
    { id: "implementation", label: "Implementation", icon: Zap },
  ]

  const handleDownload = () => {
    // Simulate PDF download
    const element = document.createElement("a")
    const file = new Blob(
      [
        `${insightDetails.title}\n\nGenerated on: ${new Date().toLocaleDateString()}\n\n${insightDetails.description}\n\nThis is a sample PDF download. In a real implementation, this would generate a comprehensive PDF report with all analysis data, charts, and recommendations.`,
      ],
      { type: "text/plain" },
    )
    element.href = URL.createObjectURL(file)
    element.download = `${insightDetails.title.replace(/\s+/g, "-").toLowerCase()}-insight-${Date.now()}.txt`
    document.body.appendChild(element)
    element.click()
    document.body.removeChild(element)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: insightDetails.title,
        text: insightDetails.description,
        url: window.location.href,
      })
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href)
      alert("Insight link copied to clipboard!")
    }
  }

  const getTrendIcon = (trend) => {
    switch (trend) {
      case "up":
        return <TrendingUp size={16} className="business-insight-trend-up" />
      case "down":
        return <TrendingUp size={16} className="business-insight-trend-down" style={{ transform: "rotate(180deg)" }} />
      default:
        return <Target size={16} className="business-insight-trend-stable" />
    }
  }

  const getInsightIcon = (type) => {
    switch (type) {
      case "positive":
        return <CheckCircle size={20} className="business-insight-icon-positive" />
      case "warning":
        return <AlertTriangle size={20} className="business-insight-icon-warning" />
      case "info":
        return <Info size={20} className="business-insight-icon-info" />
      default:
        return <Info size={20} className="business-insight-icon-info" />
    }
  }

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "business-insight-priority-high"
      case "medium":
        return "business-insight-priority-medium"
      case "low":
        return "business-insight-priority-low"
      default:
        return "business-insight-priority-medium"
    }
  }

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="business-insight-section-content">
            <div className="business-insight-overview-content">
              <h3>{insightDetails.sections.overview.title}</h3>
              <p>{insightDetails.sections.overview.content}</p>
            </div>

            <div className="business-insight-metrics-grid">
              {insightDetails.sections.overview.keyMetrics.map((metric, index) => (
                <div key={index} className="business-insight-metric-card">
                  <div className="business-insight-metric-header">
                    <span className="business-insight-metric-label">{metric.label}</span>
                    {getTrendIcon(metric.trend)}
                  </div>
                  <div className="business-insight-metric-value">{metric.value}</div>
                </div>
              ))}
            </div>
          </div>
        )

      case "analysis":
        return (
          <div className="business-insight-section-content">
            <h3>{insightDetails.sections.analysis.title}</h3>
            <p>{insightDetails.sections.analysis.content}</p>
            <div className="business-insight-charts-container">
              {insightDetails.sections.analysis.charts?.map((chart, index) => (
                <div key={index} className="business-insight-chart-card">
                  <h4>{chart.title}</h4>
                  {chart.type === "bar" && (
                    <div className="business-insight-bar-chart">
                      {chart.data.map((item, i) => (
                        <div key={i} className="business-insight-bar-item">
                          <div className="business-insight-bar-label">{item.label}</div>
                          <div className="business-insight-bar-container">
                            <div
                              className="business-insight-bar-fill"
                              style={{
                                width: `${(item.value / Math.max(...chart.data.map((d) => d.value))) * 100}%`,
                                backgroundColor: "#0e1822"
                              }}
                            ></div>
                            <span className="business-insight-bar-value">{item.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {chart.type === "line" && (
                    <div className="business-insight-line-chart">
                      {chart.data.map((item, i) => (
                        <div key={i} className="business-insight-line-item">
                          <span className="business-insight-line-label">{item.label}</span>
                          <span className="business-insight-line-value">{item.value}%</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )

      case "insights":
        return (
          <div className="business-insight-section-content">
            <h3>{insightDetails.sections.insights.title}</h3>
            <div className="business-insight-findings-container">
              {insightDetails.sections.insights.findings.map((finding, index) => (
                <div key={index} className={`business-insight-finding-card business-insight-finding-${finding.type}`}>
                  <div className="business-insight-finding-header">
                    {getInsightIcon(finding.type)}
                    <div className="business-insight-finding-title-section">
                      <h4>{finding.title}</h4>
                      <span className="business-insight-finding-impact">Impact: {finding.impact}</span>
                    </div>
                  </div>
                  <p>{finding.description}</p>
                </div>
              ))}
            </div>
          </div>
        )

      case "recommendations":
        return (
          <div className="business-insight-section-content">
            <h3>{insightDetails.sections.recommendations.title}</h3>
            <div className="business-insight-recommendations-container">
              {insightDetails.sections.recommendations.items.map((rec, index) => (
                <div key={index} className="business-insight-recommendation-card">
                  <div className="business-insight-recommendation-header">
                    <h4>{rec.title}</h4>
                    <span className={`business-insight-priority-badge ${getPriorityColor(rec.priority)}`}>
                      {rec.priority} priority
                    </span>
                  </div>
                  <p>{rec.description}</p>
                  <div className="business-insight-recommendation-details">
                    <div className="business-insight-recommendation-detail">
                      <FileText size={16} />
                      <span>Timeline: {rec.timeline}</span>
                    </div>
                    <div className="business-insight-recommendation-detail">
                      <Target size={16} />
                      <span>Expected Impact: {rec.expectedImpact}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      case "implementation":
        return (
          <div className="business-insight-section-content">
            <h3>{insightDetails.sections.implementation.title}</h3>
            <div className="business-insight-implementation-timeline">
              {insightDetails.sections.implementation.phases.map((phase, index) => (
                <div key={index} className="business-insight-implementation-phase">
                  <div className="business-insight-phase-header">
                    <div className="business-insight-phase-marker">{index + 1}</div>
                    <div className="business-insight-phase-title">
                      <h4>{phase.name}</h4>
                      <span className="business-insight-phase-timeline">{phase.timeline}</span>
                    </div>
                  </div>
                  <div className="business-insight-phase-activities">
                    <ul>
                      {phase.activities.map((activity, actIndex) => (
                        <li key={actIndex}>{activity}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )

      default:
        return <div>Select a section to view details</div>
    }
  }

  return (
    <div className="detailed-business-insight-page">
      <BackButton />

      <main className="detailed-business-insight-main">
      <h1 style={{textAlign: "center", fontWeight: "600", marginBottom: "10px"}}>REPORT</h1>
        <div className="container">
          {/* Header */}
          <div className="detailed-business-insight-header">
            <div className="business-insight-header-content">
              <div className="business-insight-header-icon">
                {insightDetails.category === "employees" ? <Users size={40} /> : <Target size={40} />}
              </div>
              <div className="business-insight-header-text">
                <h1>{insightDetails.title}</h1>
                <p>{insightDetails.description}</p>
                <div className="business-insight-header-meta">
                  <div className="business-insight-meta-item">
                    <span className={`business-priority-badge ${getPriorityColor(insightDetails.priority)}`}>
                      {insightDetails.priority} priority
                    </span>
                  </div>
                  <div className="business-insight-meta-item">
                    <span className="business-insight-category">{insightDetails.category}</span>
                  </div>
                  <div className="business-insight-meta-item">
                    <span className="business-insight-type">{insightDetails.type}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="business-insight-header-actions">
              <button className="business-insight-action-btn" onClick={handleShare}>
                <Share2 size={16} />
                Share
              </button>
              <button className="business-insight-action-btn">
                <Bookmark size={16} />
                Save
              </button>
              <button className="business-insight-download-btn" onClick={handleDownload}>
                <Download size={16} />
                Download PDF
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="business-insight-section-navigation">
            {sections.map((section) => {
              const IconComponent = section.icon
              return (
                <button
                  key={section.id}
                  className={`business-insight-nav-tab ${activeSection === section.id ? "business-insight-nav-active" : ""}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent size={16} />
                  <span>{section.label}</span>
                </button>
              )
            })}
          </div>

          {/* Content Area */}
          <div className="business-insight-content-area">{renderSection()}</div>
        </div>
      </main>
    </div>
  )
}

export default DetailedBusinessInsightPage
